import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
} from '@mui/material';

/**
 * Reusable delete confirmation dialog with type-to-confirm protection.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onConfirm: () => void | Promise<void>
 *  - title: string (e.g. "Delete Tenant")
 *  - entityName: string (what the user must type to confirm)
 *  - warningText: string (shown in Alert)
 *  - confirmButtonText: string (default "Delete")
 */
export default function ConfirmDeleteDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Delete',
  entityName = '',
  warningText = 'This action cannot be undone.',
  confirmButtonText = 'Delete',
}) {
  const [typed, setTyped] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const matches = typed.trim().toLowerCase() === entityName.trim().toLowerCase();

  const handleConfirm = async () => {
    if (!matches) return;
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          {warningText}
        </Alert>
        <Typography sx={{ mb: 2 }}>
          To confirm, type <strong>{entityName}</strong> below:
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder={entityName}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && matches && handleConfirm()}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: matches ? 'success.main' : 'error.main',
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={!matches || loading}
        >
          {loading ? 'Deleting...' : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
